// Certify every document of a child's case with the Ordem at once.
//
// A child's case needs three acts: the child's document and each parent's.
// "Certify all" previews them one after another behind one sign-in; the
// lawyer reads all of them and registers them with one click, in the same
// order, each under the number she saw. The register numbers acts in order,
// so the preview already shows the second act with the number after the first.
// Each act's steps, PDF and download stay in its own panel below.

import React, { useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import {
  CertificationActApproval,
  CertificationBatch,
  CertificationBatchAct,
  CertifiedPerson,
  PassportCertificationRecord,
  previewAllPassportCertifications,
  registerAllPassportCertifications,
} from '../../api/passportCertification';
import { RUN_POLL_MS, outcomeMessage } from './PassportCertificationPanel';
import { CertifiedDocument } from './minorCase';

export interface CertifyAllPanelProps {
  caseId: string;
  documents: CertifiedDocument[];
  records: Partial<Record<CertifiedPerson, PassportCertificationRecord | null | undefined>>;
  canCertify: boolean;
  user: User | null;
  onRefresh: () => void;
}

type Phase = 'idle' | 'previewing' | 'confirm' | 'registering';
type Message = { tone: 'error' | 'info'; text: string };

const COUNT_WORDS = ['', 'the act', 'both acts', 'all three acts'];

export const registerLabel = (count: number): string =>
  `Register ${COUNT_WORDS[count] || `all ${count} acts`}`;

const approvalOf = (act: CertificationBatchAct): CertificationActApproval | null =>
  act.status === 'preview' && act.number && act.description && typeof act.sheets === 'number'
    ? { person: act.person, number: act.number, description: act.description, sheets: act.sheets }
    : null;

const CertifyAllPanel: React.FC<CertifyAllPanelProps> = ({ caseId, documents, records, canCertify, user, onRefresh }) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [batch, setBatch] = useState<CertificationBatch | null>(null);
  const [message, setMessage] = useState<Message | null>(null);

  const working = phase === 'previewing' || phase === 'registering';
  useEffect(() => {
    if (!working) return undefined;
    const timer = setInterval(onRefresh, RUN_POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [working]);

  const open = documents.filter(d => records[d.person]?.status !== 'registered');
  if (!canCertify || documents.length < 2 || (open.length === 0 && phase === 'idle' && !message)) return null;

  const titleOf = (person: CertifiedPerson) => documents.find(d => d.person === person)?.title || person;
  const acts = batch?.acts || [];
  const approvals = acts.map(approvalOf).filter((a): a is CertificationActApproval => a !== null);
  const blocked = acts.filter(a => a.status !== 'preview' && a.status !== 'registered');

  const run = async (next: 'previewing' | 'registering', call: (token: string) => Promise<CertificationBatch>) => {
    if (!user) return;
    setMessage(null);
    setPhase(next);
    let outcome: CertificationBatch;
    try {
      outcome = await call(await user.getIdToken());
    } catch (err: any) {
      outcome = { status: 'failed', reason: err?.message || 'the portal could not start it' };
    }
    onRefresh();
    if (next === 'previewing' && outcome.acts?.length) {
      setBatch(outcome);
      setPhase('confirm');
      return;
    }
    setBatch(null);
    setPhase('idle');
    if (next === 'registering' && outcome.status === 'registered') {
      const numbers = (outcome.acts || []).filter(a => !a.already).map(a => a.registration_number);
      setMessage({ tone: 'info', text: `Registered ${numbers.join(', ')}. Sign each act's PDF in its panel below.` });
      return;
    }
    setMessage(outcomeMessage(outcome, next === 'previewing' ? 'preview' : 'register'));
  };

  const preview = () => run('previewing', token => previewAllPassportCertifications(token, caseId));
  const registerAll = () => run('registering', token => registerAllPassportCertifications(token, caseId, approvals));
  const cancel = () => {
    setBatch(null);
    setPhase('idle');
  };

  return (
    <div className="poa-cert-panel poa-cert-panel--pending oa-cert-panel" data-testid="passport-certification-all">
      <div className="oa-cert-header">
        <div>
          <h3>Ordem certification: all documents</h3>
          <p className="oa-cert-subject">{open.map(d => d.title.replace('Ordem certification: ', '')).join(', ')}</p>
        </div>
        <span className={`oa-cert-badge oa-cert-badge--${working ? 'busy' : phase}`}>
          {phase === 'previewing' ? 'Preparing' : phase === 'registering' ? 'Registering' : phase === 'confirm' ? 'Waiting for you' : 'Not certified'}
        </span>
      </div>

      {phase === 'idle' && open.length > 0 && (
        <>
          <p>
            Prepares every act below one after another, with one sign-in. You read them all and register them with one
            click.
          </p>
          <button className="financas-btn financas-btn--primary" onClick={preview} disabled={!user}>
            Certify all with the Ordem
          </button>
        </>
      )}
      {phase === 'previewing' && <p className="oa-cert-last">Preparing the acts, about 20 seconds each. Each panel below shows its steps.</p>}
      {phase === 'registering' && <p className="oa-cert-last">Registering the acts one after another. Each panel below shows its steps.</p>}

      {phase === 'confirm' && (
        <div className="oa-cert-confirm">
          {blocked.length > 0 ? (
            <p>
              Not every act can be registered: {blocked.map(a => `${titleOf(a.person)}: ${a.reason || a.status}`).join('; ')}.
              Nothing was registered.
            </p>
          ) : (
            <p>
              This is what the acts will say. They are registered in this order, under these numbers, and cannot be undone.
            </p>
          )}
          {acts.map(act => (
            <div key={act.person} data-testid={`certify-all-${act.person}`}>
              <h4>
                {titleOf(act.person)}
                {act.status === 'preview' && !act.adopted && <>: act <strong>{act.number}</strong></>}
                {act.status === 'preview' && act.adopted && <>: already in the register as <strong>{act.number}</strong>, attached</>}
                {act.already && <>: registered earlier as {act.registration_number}</>}
              </h4>
              {act.status === 'preview' && act.resumo && <div className="oa-cert-resumo">{act.resumo}</div>}
            </div>
          ))}
          <div className="oa-cert-actions">
            {blocked.length === 0 && approvals.length > 0 && (
              <button className="financas-btn financas-btn--primary" onClick={registerAll}>
                {registerLabel(approvals.length)}
              </button>
            )}
            <button className="financas-btn" onClick={cancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {message && (
        <div className={message.tone === 'error' ? 'financas-office-error' : 'oa-cert-info'} role={message.tone === 'error' ? 'alert' : 'status'}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default CertifyAllPanel;
